import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, formatError } from "../lib/api";
import { useSeo, fileUrl } from "../lib/seo";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { useRazorpay, payWithRazorpay } from "../lib/razorpay";
import ProductReviews, { Stars } from "../components/ProductReviews";

export default function ProductDetail() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [img, setImg] = useState(0);
  const [form, setForm] = useState({ quantity: 1, buyer_name: "", buyer_email: "", shipping_address: "" });
  const [paying, setPaying] = useState(false);
  const [provider, setProvider] = useState("stripe");
  const rzp = useRazorpay();
  const load = () => api.get(`/shop/products/${id}`).then((r) => setP(r.data)).catch(() => setP(false));
  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
  useSeo(p?.title, p && { "@context": "https://schema.org", "@type": "Product", name: p.title, description: p.description, sku: p.sku,
    ...(p.review_count ? { aggregateRating: { "@type": "AggregateRating", ratingValue: p.rating_avg, reviewCount: p.review_count } } : {}),
    image: (p.image_urls || []).map(fileUrl), offers: { "@type": "Offer", price: p.price_usd, priceCurrency: "USD", availability: p.stock > 0 ? "https://schema.org/InStock" : "https://schema.org/OutOfStock" } });

  if (p === false) return <div className="p-16 text-center text-[#63534E]">Product not found. <Link to="/shop" className="text-[#E65100]">Back to shop</Link></div>;
  if (!p) return <div className="p-10 text-[#63534E]">Loading…</div>;

  const buy = async (e) => {
    e.preventDefault();
    setPaying(true);
    const payload = { ...form, quantity: parseInt(form.quantity) || 1, product_id: p.id, origin_url: window.location.origin };
    if (provider === "razorpay") return payWithRazorpay("/razorpay/shop/order", payload, () => setPaying(false));
    try {
      const { data } = await api.post("/shop/checkout", payload);
      window.location.href = data.checkout_url;
    } catch (err) { toast.error(formatError(err)); setPaying(false); }
  };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const total = (p.price_usd * (parseInt(form.quantity) || 1)).toFixed(2);

  return (
    <div data-testid="product-detail" className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <Link to="/shop" className="text-sm text-[#8C6D3B]">← Sacred Shop</Link>
      <div className="mt-6 grid lg:grid-cols-2 gap-10">
        <div>
          <div className="aspect-square rounded-3xl overflow-hidden sacred-border bg-[#F3E5AB]/40">
            {p.image_urls?.[img] ? <img src={fileUrl(p.image_urls[img])} alt={p.title} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center font-devanagari text-8xl text-[#D4AF37]">ॐ</div>}
          </div>
          <div className="flex gap-2 mt-3">
            {(p.image_urls || []).map((u, i) => (
              <button key={u} onClick={() => setImg(i)} className={`w-16 h-16 rounded-lg overflow-hidden border-2 ${i === img ? "border-[#E65100]" : "border-transparent"}`}><img src={fileUrl(u)} alt="" className="w-full h-full object-cover" /></button>
            ))}
          </div>
        </div>
        <div>
          <div className="overline">{p.supplier_name}</div>
          <h1 data-testid="product-title" className="font-serif text-4xl text-[#4A0E17] mt-2 leading-tight">{p.title}</h1>
          <div className="font-mono text-3xl text-[#E65100] mt-4" data-testid="product-price">${p.price_usd.toFixed(2)}</div>
          {p.review_count > 0 && <div data-testid="product-rating" className="flex items-center gap-2 mt-2 text-sm text-[#63534E]"><Stars value={p.rating_avg} /> {p.rating_avg.toFixed(1)} · {p.review_count} review{p.review_count > 1 ? "s" : ""}</div>}
          <div className="text-sm text-[#63534E] mt-1">{p.stock > 0 ? `${p.stock} in stock` : "Sold out"}{p.sku ? ` · SKU ${p.sku}` : ""}</div>
          <p className="text-[#1F1412] mt-6 leading-relaxed whitespace-pre-line">{p.description}</p>
          {p.stock > 0 && (
            <form onSubmit={buy} className="mt-8 bg-[#FFFDF9] sacred-border rounded-2xl p-6 space-y-3">
              <div className="grid grid-cols-3 gap-3">
                <Input data-testid="buy-qty" type="number" min="1" max={p.stock} value={form.quantity} onChange={set("quantity")} className="bg-white border-[#D4AF37]/40 h-11" />
                <Input data-testid="buy-name" required placeholder="Full name" value={form.buyer_name} onChange={set("buyer_name")} className="col-span-2 bg-white border-[#D4AF37]/40 h-11" />
              </div>
              <Input data-testid="buy-email" required type="email" placeholder="Email" value={form.buyer_email} onChange={set("buyer_email")} className="bg-white border-[#D4AF37]/40 h-11" />
              <Textarea data-testid="buy-address" required rows={3} placeholder="Shipping address with PIN / ZIP" value={form.shipping_address} onChange={set("shipping_address")} className="bg-white border-[#D4AF37]/40" />
              <div className="grid grid-cols-2 gap-2">
                {[["stripe", "Card · International"], ["razorpay", rzp.enabled ? `UPI · ₹${Math.round(total * rzp.inr_per_usd)}` : "UPI · coming soon"]].map(([k, l]) => (
                  <button type="button" key={k} data-testid={`buy-provider-${k}`} disabled={k === "razorpay" && !rzp.enabled} onClick={() => setProvider(k)}
                    className={`h-10 rounded-full text-sm border transition-sacred disabled:opacity-50 ${provider === k ? "bg-[#4A0E17] text-[#F3E5AB] border-[#4A0E17]" : "bg-white text-[#4A0E17] border-[#D4AF37]/40"}`}>{l}</button>
                ))}
              </div>
              <Button data-testid="buy-submit" disabled={paying} className="w-full bg-[#E65100] hover:bg-[#BF360C] rounded-full h-12">{paying ? "Redirecting…" : `Pay $${total} securely`}</Button>
            </form>
          )}
        </div>
      </div>
      <ProductReviews productId={p.id} onChange={load} />
    </div>
  );
}
